import { Capacitor } from '@capacitor/core';
import {
  AppUpdate,
  AppUpdateResult,
  FlexibleUpdateInstallStatus,
  FlexibleUpdateState,
} from '@capawesome/capacitor-app-update';
import { AppUpdateResultCode } from '@capawesome/capacitor-app-update/dist/esm/definitions';
import toast from 'reusable-components/notifications/toast';
import { isCordova } from 'utils/platform';
import { create } from 'zustand';

type CheckForUpdateOptions = {
  startWhenAvailable: boolean;
  trigger: 'manual' | 'auto';
};

type InAppUpdateState = {
  updateAvailable: boolean;
  checkingForUpdate: boolean;
  lastAutoCheck: Date | null;
  lastManualCheck: Date | null;
  checkForUpdate: (options: CheckForUpdateOptions) => Promise<void>;
  isUpdateCheckAllowed: (trigger: 'manual' | 'auto') => boolean;
};
const isAppUpdateAvailable = async () => {
  if (isCordova()) {
    try {
      const platform = Capacitor?.getPlatform?.();
      if (platform === 'ios') {
        // @todo: disabled because not tested yet
        console.warn('iOS in-app updates not supported yet.');
        return false;
      }
      const appUpdateInfo = await AppUpdate?.getAppUpdateInfo?.();
      if (
        appUpdateInfo?.updateAvailability === 2 // UPDATE_AVAILABLE
      ) {
        return true;
      }
    } catch (error) {
      console.error('Error checking for app update:', error);
    }
  }
  return false;
};

const handleIosUpdate = async () => {
  const result = window.confirm(
    'An update is available. Do you want to update now?',
  );
  if (result) {
    // iOS does not support flexible updates, so we perform an immediate update
    await AppUpdate.openAppStore?.();
  } else {
    window.alert('You can update later from the App Store.');
  }
};

const handleAndroidUpdate = async () => {
  const appUpdateResultCode: AppUpdateResult =
    await AppUpdate?.startFlexibleUpdate();

  switch (appUpdateResultCode?.code) {
    case AppUpdateResultCode.OK:
      toast({
        text: 'Downloading app update, please wait...',
        icon: '⬇️',
        position: 'top-center',
      });
      const handleUpdateStateChange = (state: FlexibleUpdateState) => {
        if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
          AppUpdate.removeAllListeners();
          toast({
            text: 'App update downloaded, click here to install the update.',
            icon: '✅️',
            position: 'top-center',
            onClick: () => {
              AppUpdate.completeFlexibleUpdate();
            },
          });
        }
      };
      await AppUpdate.addListener(
        'onFlexibleUpdateStateChange',
        handleUpdateStateChange,
      );
      break;
  }
};

const startInAppUpdate = async (): Promise<void> => {
  const platform = Capacitor?.getPlatform?.();
  try {
    switch (platform) {
      case 'ios':
        await handleIosUpdate();
        break;
      case 'android':
        await handleAndroidUpdate();
        break;
    }
  } catch (error) {
    console.error(`Error starting in-app update.`, error);
  }
};

const MAXIMUM_AUTO_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAXIMUM_MANUAL_CHECK_INTERVAL = 3 * 1000; // 3 seconds

export const useInAppUpdateStore = create<InAppUpdateState>()((set, get) => ({
  updateAvailable: false,
  checkingForUpdate: false,
  lastAutoCheck: null,
  lastManualCheck: null,
  isUpdateCheckAllowed: (trigger: 'manual' | 'auto') => {
    if (trigger === 'manual') {
      const lastCheck = get().lastManualCheck;
      const now = new Date();
      const lastTimestamp = lastCheck?.getTime();
      const allowedTimestamp = now.getTime() - MAXIMUM_MANUAL_CHECK_INTERVAL;
      if (lastCheck && lastTimestamp > allowedTimestamp) {
        console.warn('Last manual check too soon.');
        return false;
      }
      return true;
    }
    if (trigger === 'auto') {
      const lastCheck = get().lastAutoCheck;
      const now = new Date();
      const lastTimestamp = lastCheck?.getTime();
      const allowedTimestamp = now.getTime() - MAXIMUM_AUTO_CHECK_INTERVAL;
      if (lastCheck && lastTimestamp > allowedTimestamp) {
        console.warn('Last auto check too soon.');
        return false;
      }
      return true;
    }

    return false;
  },
  checkForUpdate: async (options: CheckForUpdateOptions) => {
    const isCheckAllowed = get().isUpdateCheckAllowed(options.trigger);
    if (!isCheckAllowed) return;
    if (options.trigger === 'manual') {
      set({ lastManualCheck: new Date() });
    } else if (options.trigger === 'auto') {
      set({ lastAutoCheck: new Date() });
    }
    try {
      set({ checkingForUpdate: true });
      const result = await isAppUpdateAvailable();
      if (result) {
        if (options.startWhenAvailable) {
          await startInAppUpdate();
        } else {
          toast({
            text: 'There is an update available. Click here to start the update.',
            icon: 'ℹ️',
            position: 'top-center',
            onClick: () => {
              startInAppUpdate();
            },
          });
        }
      } else {
        if (options.trigger === 'manual') {
          toast({
            text: 'There is no update available.',
            icon: 'ℹ️',
            position: 'top-center',
          });
        }
      }
      set({ updateAvailable: result });
    } catch (error) {
      console.error('Error checking for in-app update:', error);
    }
    set({ checkingForUpdate: false });
  },
}));
